import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { fetchAbout } from '../api';
import '../styles/pages/About.scss';

const About = () => {
    usePageTitle('About');
    const [about, setAbout] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchAbout()
            .then((data) => {
                if (!cancelled) {
                    setAbout(data);
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.message);
                    setLoading(false);
                }
            });
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="page-about">
                    <div className="container text-center py-5">
                        <p className="text-muted">Loading...</p>
                    </div>
                </div>
            </>
        );
    }

    if (error || !about) {
        return (
            <>
                <Navbar />
                <div className="page-about">
                    <div className="container text-center py-5">
                        <p className="text-danger">Unable to load content. Please try again later.</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="page-about">
                {/* Hero */}
                <section className="about-hero">
                    <div className="about-hero-bg">
                        {about.hero_image_url && (
                            <img src={about.hero_image_url} alt={about.company_name} />
                        )}
                        <div className="about-hero-overlay"></div>
                    </div>
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-1"></div>
                            <div className="col-12 col-lg-8">
                                <h1 className="text-white mb-0">{about.tagline}</h1>
                            </div>
                        </div>
                    </div>
                </section>

                {/* About Content */}
                <section className="about-content">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-10">
                                <div className="row about-intro">
                                    <div className="col-12 col-md-10 col-lg-9">
                                        <p className="subtitle">{about.company_name}</p>
                                        <h3>{about.description}</h3>
                                    </div>
                                </div>

                                {/* Stats */}
                                {about.stats && about.stats.length > 0 && (
                                    <>
                                        <hr className="about-divider" />
                                        <div className="row about-stats justify-content-around text-center">
                                            {about.stats.map((stat, index) => (
                                                <div className="col-6 col-md-auto" key={index}>
                                                    <div className="number-box">
                                                        <div className="number-box-title">{stat.value}</div>
                                                        <div className="number-box-subtitle">{stat.label}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Testimonials */}
                {about.testimonials && about.testimonials.length > 0 && (
                    <section className="about-testimonials">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-4 col-xl-3">
                                    <p className="subtitle">our clients</p>
                                    <h2>What our clients say about us</h2>
                                </div>
                                <div className="d-none d-lg-block col-lg-1 col-xl-2"></div>
                                <div className="col-12 col-lg-6">
                                    <div className="testimonials-list">
                                        {about.testimonials.map((item, index) => (
                                            <div className="media media-review" key={index}>
                                                {item.avatar_url && (
                                                    <img
                                                        src={item.avatar_url}
                                                        className="media-img rounded-circle"
                                                        alt={item.name}
                                                    />
                                                )}
                                                <div className="media-body">
                                                    <h5>{item.name}</h5>
                                                </div>
                                                <p>" {item.text} "</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Contact & Social CTA */}
                <section className="about-cta">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-12 col-xl-10">
                                <div className="row align-items-center justify-content-center justify-content-sm-between">
                                    <div className="col-md col-lg-6">
                                        {about.contact && (
                                            <div className="about-contact-info">
                                                {about.contact.email && <p>{about.contact.email}</p>}
                                                {about.contact.phone && <p>{about.contact.phone}</p>}
                                                {about.contact.address && <p>{about.contact.address}</p>}
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-auto">
                                        {about.social_links && about.social_links.length > 0 && (
                                            <div className="about-social-links">
                                                {about.social_links.map((link, i) => (
                                                    <a
                                                        key={i}
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-outline-dark btn-social"
                                                    >
                                                        {link.platform}
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer />
        </>
    );
};

export default About;
